import React, { Dispatch, SetStateAction } from "react"
import { useSelector } from "react-redux"
import { useDeviceType } from "utils/MediaQuery"
import { AppState } from "store/store"
import { loadResponseEasyCalc } from "utils/JsonManipulation"
import { EasyCalcResIf } from "utils/ifs/EasyCalcRes"
import CommonHelp from "components/common/CommonHelp"
import { MODAL_TYPE } from "components/Modal/ModalSelector"
import { DriversLicenseColor } from "Const"
import styles from "./styles.module.scss"

const formatNumber = (num: string | number) => {
  const numString = typeof num === "number" ? num.toString() : num
  return numString.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
}

const DOT = "・"
const FigureInRed = "#ff3700"
const FigureInBlue = "#165bc2"
// サブコンポーネント
interface HelpIconProps {
  text: string
  click: () => void
}
const HelpIcon = ({ text, click }: HelpIconProps) => {
  return (
    <div className={styles.helpContainer}>
      <CommonHelp text={text} click={click} />
    </div>
  )
}
const CheckIcon = () => {
  return (
    <div className={styles.checkIcon_container}>
      <div id="checkIcon" className={styles.checkIcon} />
    </div>
  )
}
interface OpacifiableSpanProps {
  style?: string
  css?: React.CSSProperties
  unit?: string
  text: React.ReactNode
}
const OpacifiableSpan = ({ style = "", css = {}, unit = "", text }: OpacifiableSpanProps) => {
  return (
    <span
      className={`${styles.itemContainer__ml} ${styles.isOpacifiable} ${style} ${styles.marginTopBottom}`}
      style={css}
    >
      {text}
      {unit !== "" && <span style={{ color: "#333333", fontSize: 12, marginLeft: "3px" }}>{unit}</span>}
    </span>
  )
}
interface ItemContainerProps {
  classNames: Array<string>
  children: React.ReactNode
  style?: React.CSSProperties
}
const ItemContainer = ({ classNames, children, style = {} }: ItemContainerProps) => {
  return (
    <div className={`${styles.itemContainer} ${classNames.join(" ")}`} style={style}>
      {children}
    </div>
  )
}
// メインコンポーネント
const CAR_INSURANCE_TYPE = {
  車両不担保: "00",
  一般タイプ: "01",
  限定タイプ: "05"
}
const SCOPE = {
  NO_LIMIT: "0",
  LIMITED: "1"
}
interface Props {
  plan: string
  isAnnual: boolean
  setOpenModal: Dispatch<SetStateAction<boolean>>
  setModalType: Dispatch<SetStateAction<string>>
}
/**
 * Step4見積結果画面の補償内容欄
 * @param {string} plan - カード上に表示するプラン
 * @param {number} isAnnual - 一括払保険料フラグ
 * @param {Dispatch<SetStateAction<boolean>>} setOpenModal - モーダル表示・非表示のスイッチ
 * @param {Dispatch<SetStateAction<string>>} setModalType - モーダルの表示タイプ
 */
const PlanBody = ({ plan, isAnnual, setOpenModal, setModalType }: Props) => {
  // 必要に応じて、タブレット、モバイル（スマホ・タブレット共通）、PC向けに設定が必要であれば利用
  const { isSmartPhone, isTablet, isMobile, isPc } = useDeviceType()
  const licenceColor = useSelector((state: AppState) => state.userInfo.licenceColor)
  const res: EasyCalcResIf = useSelector((state: AppState) => state.plan.plan)
  const data = loadResponseEasyCalc(res)
  // eslint-disable-next-line no-nested-ternary
  const planData = plan === "light" ? data.light : plan === "standard" ? data.standard : data.fullSupport
  const renderGrayOut = (isGrayOut: boolean) => {
    let style = !isGrayOut ? styles.opacity_text : ``
    style = !isGrayOut ? `${style} ${styles.opacity_full}` : style
    return style
  }
  const getPersonalInjuryScope = (scope: string) => {
    if (scope === SCOPE.NO_LIMIT) {
      return "車内・車外"
    }
    if (scope === SCOPE.LIMITED) {
      return "車内のみ"
    }
    return ""
  }
  const getCarInsuranceType = (str: string) => {
    if (str === CAR_INSURANCE_TYPE.車両不担保) return ""
    if (str === CAR_INSURANCE_TYPE.一般タイプ) return "フルカバー"
    if (str === CAR_INSURANCE_TYPE.限定タイプ) return ""
    return ""
  }
  return (
    <div className={styles.root}>
      <div>
        <h1 className={styles.titleContainer__title}>補償内容</h1>
        <div className={styles.titleContainer__tagLine}>
          <CheckIcon />
          項目はこのプランの補償内容です
        </div>
      </div>
      <div>
        <h3>基本補償</h3>
        <ItemContainer classNames={[renderGrayOut(planData.basicComp.personalAndObject)]}>
          <CheckIcon />
          <OpacifiableSpan text="対人・対物賠償" />
          <HelpIcon
            text=""
            click={() => {
              setOpenModal(true)
              setModalType(MODAL_TYPE.PERSONAL_AND_OBJECT)
            }}
          />
          <div className={styles.itemContainer__value}>
            <span>{planData.basicComp.personalAndObject && "無制限"}</span>
          </div>
        </ItemContainer>
        <ItemContainer classNames={[renderGrayOut(planData.otherComps.objectTotalLoss)]}>
          <CheckIcon />
          <OpacifiableSpan text="対物全損時修理差額費用特約" />
          <HelpIcon
            text=""
            click={() => {
              setOpenModal(true)
              setModalType(MODAL_TYPE.OBJECT_TOTAL_LOSS)
            }}
          />
        </ItemContainer>
        <ItemContainer classNames={[renderGrayOut(planData.basicComp.uninsuredCarAccidents)]}>
          <CheckIcon />
          <OpacifiableSpan text="無保険車傷害" />
          <HelpIcon
            text=""
            click={() => {
              setOpenModal(true)
              setModalType(MODAL_TYPE.UNINSURED_CAR_ACCIDENTS)
            }}
          />
          <div className={styles.itemContainer__value}>
            <span>{planData.basicComp.uninsuredCarAccidents && "無制限"}</span>
          </div>
        </ItemContainer>
        <ItemContainer classNames={[renderGrayOut(planData.basicComp.otherCarDriving)]}>
          <CheckIcon />
          <OpacifiableSpan text="他車運転特約" />
          <HelpIcon
            text=""
            click={() => {
              setOpenModal(true)
              setModalType(MODAL_TYPE.OTHER_CAR_DRIVING)
            }}
          />
        </ItemContainer>
        <ItemContainer classNames={[renderGrayOut(planData.basicComp.victimSaving)]}>
          <CheckIcon />
          <OpacifiableSpan text="被害者救済費用特約" />
          <HelpIcon
            text=""
            click={() => {
              setOpenModal(true)
              setModalType(MODAL_TYPE.VICTIM_SAVING)
            }}
          />
        </ItemContainer>
      </div>
      <div>
        <h3>各種割引</h3>
        <ItemContainer classNames={[renderGrayOut(planData.discounts.internet)]} style={{ flexWrap: "wrap" }}>
          <CheckIcon />
          <OpacifiableSpan style={styles.itemContainer__internet} text="ネット割" />
          <HelpIcon
            text=""
            click={() => {
              setOpenModal(true)
              setModalType(MODAL_TYPE.INTERNET_DISCOUNT)
            }}
          />
        </ItemContainer>
        <ItemContainer classNames={[renderGrayOut(false)]}>
          <CheckIcon />
          <OpacifiableSpan text="20等級継続割引" />
          <HelpIcon
            text=""
            click={() => {
              setOpenModal(true)
              setModalType(MODAL_TYPE.GRADE_20_NO_CLAIM_DISCOUNT)
            }}
          />
        </ItemContainer>
        <ItemContainer classNames={[renderGrayOut(planData.discounts.noAccident)]} style={{ flexWrap: "wrap" }}>
          <CheckIcon />
          <OpacifiableSpan text="無事故割引" />
          <HelpIcon
            text=""
            click={() => {
              setOpenModal(true)
              setModalType(MODAL_TYPE.NO_ACCIDENT_DISCOUNT)
            }}
          />
        </ItemContainer>
        <ItemContainer classNames={[renderGrayOut(planData.discounts.EV_HV)]} style={{ flexWrap: "wrap" }}>
          <CheckIcon />
          <OpacifiableSpan text="電気・ハイブリッド車割引" />
          <HelpIcon
            text=""
            click={() => {
              setOpenModal(true)
              setModalType(MODAL_TYPE.EV_HV)
            }}
          />
        </ItemContainer>
        <ItemContainer classNames={[renderGrayOut(planData.otherComps.ASVDiscount)]}>
          <CheckIcon />
          <OpacifiableSpan text="自動ブレーキ（ASV）割引" />
          <HelpIcon
            text=""
            click={() => {
              setOpenModal(true)
              setModalType(MODAL_TYPE.ASV_DISCOUNT)
            }}
          />
        </ItemContainer>
        <ItemContainer classNames={[renderGrayOut(planData.otherComps.newCarDiscount)]}>
          <CheckIcon />
          <OpacifiableSpan text="新車割引" />
          <HelpIcon
            text=""
            click={() => {
              setOpenModal(true)
              setModalType(MODAL_TYPE.NEW_CAR_DISCOUNT)
            }}
          />
        </ItemContainer>
        <ItemContainer classNames={[renderGrayOut(licenceColor === DriversLicenseColor.Gold.toString())]}>
          <CheckIcon />
          <OpacifiableSpan text="ゴールド免許割引" />
          <HelpIcon
            text=""
            click={() => {
              setOpenModal(true)
              setModalType(MODAL_TYPE.GOLD_LICENSE_DISCOUNT)
            }}
          />
        </ItemContainer>
        <ItemContainer classNames={[renderGrayOut(planData.discounts.secondCar)]} style={{ flexWrap: "wrap" }}>
          <CheckIcon />
          <OpacifiableSpan text="おとなの2台目割引" />
          <HelpIcon
            text=""
            click={() => {
              setOpenModal(true)
              setModalType(MODAL_TYPE.SECOND_CAR)
            }}
          />
        </ItemContainer>
      </div>
      <div>
        <h3>おとなの早割</h3>
        <ItemContainer classNames={[renderGrayOut(planData.earlyPaymentDiscount.fiftyDays)]}>
          <CheckIcon />
          <OpacifiableSpan text="早割50日" />
          <HelpIcon
            text=""
            click={() => {
              setOpenModal(true)
              setModalType(MODAL_TYPE.EARLY_PAYMENT_DISCOUNT_FIFTY_DAYS)
            }}
          />
        </ItemContainer>
        <ItemContainer classNames={[renderGrayOut(planData.earlyPaymentDiscount.thirtyDays)]}>
          <CheckIcon />
          <OpacifiableSpan text="早割30日" />
          <HelpIcon
            text=""
            click={() => {
              setOpenModal(true)
              setModalType(MODAL_TYPE.EARLY_PAYMENT_DISCOUNT_THIRTY_DAYS)
            }}
          />
        </ItemContainer>
      </div>
      <div className={styles.root__attachedServices}>
        <h3>付帯サービス</h3>
        <ItemContainer classNames={[renderGrayOut(planData.attachedServices.ALSOK)]}>
          <CheckIcon />
          <OpacifiableSpan text="ALSOK事故現場安心サポート" />
          <HelpIcon
            text=""
            click={() => {
              setOpenModal(true)
              setModalType(MODAL_TYPE.ALSOK)
            }}
          />
        </ItemContainer>
        <div className={`${styles.itemContainer__alsok}  ${renderGrayOut(planData.attachedServices.ALSOK)}`}>
          <OpacifiableSpan
            text="ALSOK隊員が事故現場へかけつけ、現場の安全確保、救急車の手配、事故状況の聴取・報告などの事故対応サポートを行うサービスが自動セットされています。"
            style={styles.itemContainer__paper}
            css={{ marginLeft: 0 }}
          />
        </div>
      </div>
      <div>
        <h3>ご自身や同乗者への補償</h3>
        <ItemContainer
          classNames={[renderGrayOut(planData.forYourselfAndPassengers.personalInjury.comp >= 0)]}
          style={{ paddingBottom: 0 }}
        >
          <CheckIcon />
          <OpacifiableSpan text="人身傷害" />
          <HelpIcon
            text=""
            click={() => {
              setOpenModal(true)
              setModalType(MODAL_TYPE.PERSONAL_INJURY)
            }}
          />
        </ItemContainer>
        <ul className={styles.itemContainer__personalInjury_list}>
          <li>
            <div>
              <span className={styles.isOpacifiable}>{DOT}補償範囲</span>
              <span className={styles.isOpacifiable}>
                {getPersonalInjuryScope(planData.forYourselfAndPassengers.personalInjury.coverage)}
              </span>
            </div>
          </li>
          <li>
            <div>
              <span className={styles.isOpacifiable}>{DOT}保険金額（1名につき）</span>
              {planData.forYourselfAndPassengers.personalInjury.comp > 0 ? (
                <OpacifiableSpan
                  text={formatNumber(planData.forYourselfAndPassengers.personalInjury.comp)}
                  unit="万円"
                  css={{ color: FigureInRed }}
                />
              ) : (
                <span className={styles.isOpacifiable}>無制限</span>
              )}
            </div>
          </li>
        </ul>
        <ItemContainer
          classNames={[renderGrayOut(planData.forYourselfAndPassengers.passengerInjuryComp !== 0)]}
          style={{ paddingBottom: 0 }}
        >
          <CheckIcon />
          <OpacifiableSpan text="搭乗者傷害" />
          <HelpIcon
            text=""
            click={() => {
              setOpenModal(true)
              setModalType(MODAL_TYPE.PASSENGER_INJURY)
            }}
          />
        </ItemContainer>
        <ul
          className={`${styles.itemContainer__passengerInjury_list} ${renderGrayOut(
            planData.forYourselfAndPassengers.passengerInjuryComp !== 0
          )}`}
        >
          <li>
            <div>
              <span className={styles.isOpacifiable}>{DOT}保険金額（1名につき）</span>
              <span className={styles.isOpacifiable} style={{ padding: "2px 0px 1px" }}>
                {planData.forYourselfAndPassengers.passengerInjuryComp === 0 ? (
                  ""
                ) : (
                  <OpacifiableSpan
                    text={formatNumber(planData.forYourselfAndPassengers.passengerInjuryComp)}
                    unit="万円"
                    css={{ color: FigureInRed }}
                  />
                )}
              </span>
            </div>
          </li>
        </ul>
      </div>
      <div>
        <h3>ご自身の車への補償</h3>
        <ItemContainer classNames={[renderGrayOut(planData.forYourCar.comp !== null)]} style={{ paddingBottom: 0 }}>
          <CheckIcon />
          <OpacifiableSpan text="車両保険" />
          <HelpIcon
            text=""
            click={() => {
              setOpenModal(true)
              setModalType(MODAL_TYPE.FOR_YOUR_CAR__TYPE)
            }}
          />
        </ItemContainer>
        <ul className={`${styles.itemContainer__forYourCar_list} ${renderGrayOut(planData.forYourCar.comp !== null)}`}>
          <li key="type" className={renderGrayOut(planData.forYourCar.type !== CAR_INSURANCE_TYPE.車両不担保)}>
            <div className={styles.itemContainer__forYourCar_list_child}>
              <OpacifiableSpan text={`${DOT}保険タイプ`} />
              <span style={{ marginLeft: "auto" }}>{getCarInsuranceType(planData.forYourCar.type)}</span>
            </div>
          </li>
          <li key="comp">
            <div className={styles.itemContainer__forYourCar_list_child}>
              <OpacifiableSpan text={`${DOT}保険金額`} />
              {planData.forYourCar.comp && (
                <OpacifiableSpan
                  text={formatNumber(planData.forYourCar.comp)}
                  unit="万円"
                  css={{ color: FigureInRed }}
                />
              )}
            </div>
          </li>
          <li key="selfResp">
            <div className={styles.itemContainer__forYourCar_list_child}>
              <OpacifiableSpan text={`${DOT}自己負担額`} />
            </div>
            <div className={styles.itemContainer__forYourCar_deductible}>
              <div>
                <OpacifiableSpan text="1回目" css={{ marginLeft: "14px" }} />
                {planData.forYourCar.deductible1 !== null && (
                  <OpacifiableSpan
                    text={formatNumber(planData.forYourCar.deductible1)}
                    unit="万円"
                    css={{ color: FigureInRed, padding: "3px 0px 1px" }}
                  />
                )}
              </div>
              <div>
                <OpacifiableSpan text="2回目以降" css={{ marginLeft: "14px" }} />
                {planData.forYourCar.deductible2 !== null && (
                  <OpacifiableSpan
                    text={formatNumber(planData.forYourCar.deductible2)}
                    unit="万円"
                    css={{ color: FigureInRed, padding: "3px 0px 1px" }}
                  />
                )}
              </div>
            </div>
          </li>
        </ul>
        <ItemContainer
          classNames={[renderGrayOut(planData.forYourCar.originalPriceComp.isAttached)]}
          style={{ paddingBottom: 0 }}
        >
          <CheckIcon />
          <OpacifiableSpan text="車両新価特約" />
          <HelpIcon
            text=""
            click={() => {
              setOpenModal(true)
              setModalType(MODAL_TYPE.FOR_YOUR_CAR__ORIGINAL_PRICE_COMPENSATION)
            }}
          />
        </ItemContainer>
        <ul
          className={`${styles.itemContainer__itemList} ${renderGrayOut(
            planData.forYourCar.originalPriceComp.isAttached
          )}`}
        >
          <li key="pricing" className={renderGrayOut(planData.forYourCar.originalPriceComp.isAttached)}>
            <div className={styles.itemContainer__itemList_child}>
              <OpacifiableSpan text={`${DOT}新車価格相当額`} />
              {planData.forYourCar.originalPriceComp.isAttached &&
                planData.forYourCar.originalPriceComp.pricing !== 0 && (
                  <OpacifiableSpan
                    text={formatNumber(planData.forYourCar.originalPriceComp.pricing)}
                    unit="万円"
                    css={{ color: FigureInRed, padding: "3px 0px 1px" }}
                  />
                )}
            </div>
          </li>
        </ul>
        <ItemContainer classNames={[renderGrayOut(planData.forYourCar.totalLossComp.isAttached)]}>
          <CheckIcon />
          <OpacifiableSpan text="車両全損修理時特約" />
          <HelpIcon
            text=""
            click={() => {
              setOpenModal(true)
              setModalType(MODAL_TYPE.FOR_YOUR_CAR__TOTAL_LOSS_COMPENSATION)
            }}
          />
        </ItemContainer>
        <ItemContainer
          classNames={[renderGrayOut(planData.forYourCar.replacementCar.isAttached)]}
          style={{ flexWrap: "wrap", paddingBottom: 0 }}
        >
          <CheckIcon />
          <OpacifiableSpan text="事故時代車費用特約" />
          <HelpIcon
            text=""
            click={() => {
              setOpenModal(true)
              setModalType(MODAL_TYPE.FOR_YOUR_CAR__REPLACEMENT_CAR)
            }}
          />
        </ItemContainer>
        <ul
          className={`${styles.itemContainer__itemList} ${renderGrayOut(
            planData.forYourCar.replacementCar.isAttached
          )}`}
          style={{ borderBottom: "1px solid #bebebe" }}
        >
          <li key="pricing" className={renderGrayOut(planData.forYourCar.replacementCar.isAttached)}>
            <div className={styles.itemContainer__itemList_child}>
              <OpacifiableSpan text={`${DOT}支払限度日額`} />
              {planData.forYourCar.replacementCar.isAttached && (
                <OpacifiableSpan text="10,000" unit="円" css={{ color: FigureInRed, padding: "3px 0px 1px" }} />
              )}
            </div>
          </li>
        </ul>
      </div>
      <div>
        <h3>その他の補償</h3>
        <ItemContainer classNames={[renderGrayOut(planData.otherComps.roadAssitance)]}>
          <CheckIcon />
          <OpacifiableSpan text="ロードアシスタンス特約" />
          <HelpIcon
            text=""
            click={() => {
              setOpenModal(true)
              setModalType(MODAL_TYPE.ROAD_ASSISTANCE)
            }}
          />
        </ItemContainer>
        <ItemContainer classNames={[renderGrayOut(planData.otherComps.lawyerCost)]}>
          <CheckIcon />
          <OpacifiableSpan text="弁護士費用特約" />
          <HelpIcon
            text=""
            click={() => {
              setOpenModal(true)
              setModalType(MODAL_TYPE.LAWYER_COST)
            }}
          />
        </ItemContainer>
        <ItemContainer classNames={[renderGrayOut(planData.otherComps.personalLiability)]} style={{ display: "block" }}>
          <div style={{ display: "flex" }}>
            <CheckIcon />
            <OpacifiableSpan text="個人賠償責任特約" />
            <HelpIcon
              text=""
              click={() => {
                setOpenModal(true)
                setModalType(MODAL_TYPE.PERSONAL_LIABILITY)
              }}
            />
          </div>
          <OpacifiableSpan text="（示談交渉サービス付き）" css={{ display: "block" }} />
        </ItemContainer>
        <ItemContainer classNames={[renderGrayOut(planData.otherComps.bicycleDamage)]}>
          <CheckIcon />
          <OpacifiableSpan text="自転車傷害特約" />
          <HelpIcon
            text=""
            click={() => {
              setOpenModal(true)
              setModalType(MODAL_TYPE.BICYCLE_DAMAGE)
            }}
          />
        </ItemContainer>
        <ItemContainer classNames={[renderGrayOut(planData.otherComps.familyBike)]}>
          <CheckIcon />
          <OpacifiableSpan text="ファミリーバイク特約" />
          <HelpIcon
            text=""
            click={() => {
              setOpenModal(true)
              setModalType(MODAL_TYPE.FAMILY_BIKE)
            }}
          />
        </ItemContainer>
        <ItemContainer classNames={[renderGrayOut(planData.otherComps.accessaryDiscount)]} style={{ paddingBottom: 0 }}>
          <CheckIcon />
          <OpacifiableSpan text="車両身の回り品補償" />
          <HelpIcon
            text=""
            click={() => {
              setOpenModal(true)
              setModalType(MODAL_TYPE.ACCESSARY_DISCOUNT)
            }}
          />
        </ItemContainer>
        <ul
          className={`${styles.itemContainer__itemList} ${renderGrayOut(planData.otherComps.accessaryDiscount)}`}
          style={{ borderBottom: "1px solid #bebebe" }}
        >
          <li key="pricing" className={renderGrayOut(planData.otherComps.accessaryDiscount)}>
            <div className={styles.itemContainer__itemList_child}>
              <OpacifiableSpan text={`${DOT}保険金額`} />
              {planData.otherComps.accessaryDiscount && (
                <OpacifiableSpan text="50" unit="万円" css={{ color: FigureInRed, padding: "3px 0px 1px" }} />
              )}
            </div>
          </li>
        </ul>
      </div>
      <div className={styles.root__insurancePolicy}>
        <h3>保険証券</h3>
        <ItemContainer classNames={[renderGrayOut(planData.insurancePolicy.paper)]}>
          <CheckIcon />
          <OpacifiableSpan text="デジタル保険証券" />
          <HelpIcon
            text=""
            click={() => {
              setOpenModal(true)
              setModalType(MODAL_TYPE.PAPER)
            }}
          />
        </ItemContainer>
        <div
          className={`${styles.itemContainer__policy_explanation}  ${renderGrayOut(planData.insurancePolicy.paper)}`}
        >
          <OpacifiableSpan
            text="すべてのお客さまにデジタル保険証券を無料で発行いたします。
            デジタル保険証券はマイページ上で確認・印刷できるPDF形式の保険証券です。"
            style={styles.itemContainer__paper}
            css={{ marginLeft: 0 }}
          />
        </div>
      </div>
    </div>
  )
}

export default PlanBody
